import { PublicHostedAuthSession } from '@unipile/provider-shared';
import type { ProviderAuthenticationRequestCheckpointResponse, RequestCheckpointBody } from '@unipile/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '../../../libs/axios';
import type { MutationConfig } from '../../../types/react-query-utility';

/**
 * This hook must live in UI package because it's used in provider packages.
 */

export const requestCheckpoint = async (
  { data }: { data: RequestCheckpointBody },
  { signal }: { signal?: AbortSignal } = {},
): Promise<ProviderAuthenticationRequestCheckpointResponse> => {
  const response = await axiosInstance.post<ProviderAuthenticationRequestCheckpointResponse>(
    `/v2/auth/checkpoint/request`,
    data,
    {
      signal,
    },
  );

  return response.data;
};

type UseRequestCheckpointOptions = {
  mutationConfig?: MutationConfig<typeof requestCheckpoint>;
  /**
   * Resolved when the mutation runs. Use when the abort controller is recreated (e.g. in an effect)
   * so Strict Mode’s effect re-run does not keep using an already-aborted signal.
   */
  getSignal?: () => AbortSignal | undefined;
};

export const useRequestCheckpoint = ({ mutationConfig, getSignal }: UseRequestCheckpointOptions = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};

  const q = useQueryClient();

  return useMutation({
    onSuccess: (...args) => {
      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: ({
      data,
    }: {
      data: Omit<RequestCheckpointBody, 'state'>;
    }): Promise<ProviderAuthenticationRequestCheckpointResponse> => {
      // Result of useHostedAuthPayload should be in react-query's cache as it's the first query made when loading the page
      const session = q.getQueryData<PublicHostedAuthSession>(['session']);
      if (!session) throw new Error('Session not in cache');

      return requestCheckpoint({ data: { ...data, ...(session.state && { state: session.state }) } }, { signal: getSignal?.() });
    },
  });
};
